/**
 * 리셀러 콘솔 메뉴·주소별 필요한 권한(11-02 리셀러 직원 계정). 화면은 숨김·이동만 — 막기는 서버(inapi)가 한다.
 * access = introspect profile 에서 뽑은 { perms, owner, customerIds }.
 *  - perms null = 아직 모름·옛 inapi → 다 보임(지금과 같게)
 *  - customerIds 배열 = 담당 고객사만 보는 직원
 */

/** 위에서부터 처음 맞는 규칙 하나만 본다(구체적인 주소가 먼저) */
const RULES = [
  // 직원 계정 API 는 서버가 모드와 상관없이 늘 봄 → 화면도 늘 권한대로
  { re: /^\/staff(\/|$)/, perm: 'reseller.staff.manage', always: true },
  // 결제 서버·콜봇 서버는 담당 고객사를 모름 → 담당이 정해진 직원은 못 엶
  {
    re: /^\/subscriptions\/payment-history(\/|$)/,
    perm: 'reseller.billing.view',
    allCustomers: true,
  },
  {
    re: /^\/callbot-usage-fees(\/|$)/,
    perm: 'reseller.billing.view',
    allCustomers: true,
  },
  {
    re: /^\/subscriptions\/(chatbot|voicebot|centerflow)\/(create|[^/]+\/edit)$/,
    perm: 'reseller.billing.manage',
  },
  { re: /^\/subscriptions(\/|$)/, perm: 'reseller.billing.view' },
  {
    re: /^\/(customers|dashboard|integration)(\/|$)/,
    perm: 'reseller.customer.view',
  },
];

/** 이 권한 코드가 있나(perms 모름·대표면 있음) */
export const hasPerm = (access, code) =>
  !access ||
  !Array.isArray(access.perms) ||
  !!access.owner ||
  access.perms.includes(code);

/** 버튼용: 권한 모드가 enforce 일 때만 권한대로(아니면 서버가 안 막으니 보임) */
export const canDo = (access, code) =>
  !access?.enforce || hasPerm(access, code);

/** 담당 고객사가 정해진 직원인가 */
export const limitedCustomers = (access) => Array.isArray(access?.customerIds);

/** 이 주소를 열 수 있나. 규칙 없는 주소(보안 설정·2단계 인증 등)는 늘 열림 */
export function canOpen(path, access) {
  const rule = RULES.find((r) => r.re.test(path));
  if (!rule) return true;
  if (rule.allCustomers && limitedCustomers(access)) return false;
  // 권한 모드가 enforce 가 아니면 서버가 안 막으니 화면도 보임(규칙 PERMISSIONS 7)
  if (!rule.always && !access?.enforce) return true;
  return hasPerm(access, rule.perm);
}

/** 들어갈 첫 화면(고객 목록 → 구독 → 직원 계정 → 암호 변경 순으로 열리는 곳) */
export function homePath(access) {
  return (
    ['/customers', '/subscriptions/chatbot', '/staff'].find((p) =>
      canOpen(p, access)
    ) ?? '/security/change-password'
  );
}

/**
 * introspect 응답 → access. 권한 모드(modes.perm — 대표가 직접 고른 직원은 늘 enforce)를 따른다:
 * off(비상 해제) = 서버도 안 막으니 다 보임, log = 직원 계정 메뉴·담당 고객사만 거름, enforce = 권한대로
 */
export function accessFromIntrospect(data) {
  const p = data?.profile ?? {};
  const m = data?.modes?.perm;
  const mode = typeof m === 'string' ? m : m?.mode;
  if (mode === 'off')
    return { perms: null, owner: false, customerIds: null, enforce: false };
  return {
    perms: Array.isArray(p.perms) ? p.perms : null,
    owner: !!p.owner,
    customerIds: Array.isArray(p.customerIds) ? p.customerIds : null,
    enforce: mode === 'enforce',
  };
}

/** 「쓰기·관리」를 고르면 같은 메뉴 「보기」도 켬(서버 규칙 REQUIRES_VIEW 와 같음). catalog = [{ code, requires }] */
export function withRequires(codes, catalog) {
  const req = new Map((catalog ?? []).map((c) => [c.code, c.requires]));
  const out = new Set(codes);
  for (const c of codes) if (req.get(c)) out.add(req.get(c));
  return [...out].sort();
}
